/**
 * Admin panel section definitions with permission checks.
 * Extracted to a plain JS file so it can be imported by both
 * AdminLayout (JSX) and test files without JSX parsing.
 *
 * Each section carries a `group` used to render labelled groups in the
 * sidebar (the empty-string group is the landing area, no header). i18n:
 * since this is a module-level constant (no hook scope), `labelKey`/`groupKey`
 * hold translation keys resolved via `t()` in the rendering component; the
 * literal `group` string stays as the grouping/comparison key.
 */
const ADMIN_SECTIONS = [
  // Landing — system health dashboard
  {
    path: '',
    labelKey: 'admin.navOverview',
    group: '',
    check: (p) => p.isOwner,
  },

  // INNEHÅLL — content management
  {
    path: 'news',
    labelKey: 'nav.news',
    group: 'INNEHÅLL',
    groupKey: 'admin.groupContent',
    check: (p) => p.isOwner || p.hasAnyCommand('news.manage'),
  },
  {
    path: 'shop',
    labelKey: 'nav.shop',
    group: 'INNEHÅLL',
    groupKey: 'admin.groupContent',
    check: (p) => p.isOwner || p.hasAnyCommand('shop.manage'),
  },
  {
    // Temalabbet: granska teman på riktiga sidor + mät kontrast i webbläsaren.
    // Gated på shop.manage och inte en egen behörighet — teman ÄR butiksvaror,
    // och den som listar dem är den som justerar dem.
    path: 'themes',
    labelKey: 'admin.themeLab.nav',
    group: 'INNEHÅLL',
    groupKey: 'admin.groupContent',
    check: (p) => p.isOwner || p.hasAnyCommand('shop.manage'),
  },
  {
    path: 'maps',
    labelKey: 'admin.navMaps',
    group: 'INNEHÅLL',
    groupKey: 'admin.groupContent',
    check: (p) => p.isOwner,
  },
  {
    path: 'medals',
    labelKey: 'admin.navMedals',
    group: 'INNEHÅLL',
    groupKey: 'admin.groupContent',
    check: (p) => p.hasAnyCommand('admin.tournament', 'tournament.create', 'tournament.manage'),
  },

  // SPEL & TÄVLING — match play and competition
  {
    path: 'matches',
    labelKey: 'admin.navMatches',
    group: 'SPEL & TÄVLING',
    groupKey: 'admin.groupCompetition',
    check: (p) => p.hasAnyCommand('admin.renumber-match', 'admin.delete-stats', 'admin.reset-counter', 'admin.import-stats', 'admin.force-drop', 'match.demos'),
  },
  {
    path: 'elo',
    labelKey: 'admin.navElo',
    group: 'SPEL & TÄVLING',
    groupKey: 'admin.groupCompetition',
    check: (p) => p.hasAnyCommand('admin.recalculate', 'admin.new-season', 'rank.process'),
  },
  {
    path: 'tournaments',
    labelKey: 'admin.navTournaments',
    group: 'SPEL & TÄVLING',
    groupKey: 'admin.groupCompetition',
    check: (p) => p.hasAnyCommand('admin.tournament', 'tournament.create', 'tournament.manage'),
  },
  {
    path: 'drops',
    labelKey: 'admin.navDrops',
    group: 'SPEL & TÄVLING',
    groupKey: 'admin.groupCompetition',
    check: (p) => p.hasAnyCommand('admin.drop-config', 'admin.force-drop', 'drop.add'),
  },
  {
    path: 'queue',
    labelKey: 'admin.navQueue',
    group: 'SPEL & TÄVLING',
    groupKey: 'admin.groupCompetition',
    check: (p) => p.hasAnyCommand('queue.kick', 'queue.clear'),
  },

  // SPELARE & ÅTKOMST — player data and access control
  {
    path: 'players',
    labelKey: 'nav.players',
    group: 'SPELARE & ÅTKOMST',
    groupKey: 'admin.groupPlayers',
    check: (p) => p.hasAnyCommand('manager.register'),
  },
  {
    // Merged container: Spelaråtkomst + Invites + Behörigheter + Web-roller + Användare
    path: 'access-roles',
    labelKey: 'admin.navAccessRoles',
    group: 'SPELARE & ÅTKOMST',
    groupKey: 'admin.groupPlayers',
    check: (p) => p.isOwner || p.hasAnyCommand('access.grant', 'admin.permissions'),
  },

  // SYSTEM — configuration, tooling, diagnostics
  {
    path: 'settings',
    labelKey: 'admin.navSettings',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.hasAnyCommand('admin.configure'),
  },
  {
    // Site-wide status/maintenance banners ("driftnotiser")
    path: 'notices',
    labelKey: 'admin.navNotices',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner || p.hasAnyCommand('notices.manage'),
  },
  {
    // Poängekonomins mätvy: penningmängd, faucets vs sinks, fördelning
    path: 'economy',
    labelKey: 'admin.navEconomy',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner || p.hasAnyCommand('shop.manage'),
  },
  {
    // New hub gathering the scattered dev/test tools (demo-match, queue-fill)
    path: 'verktyg',
    labelKey: 'admin.navTools',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner,
  },
  {
    // Merged container: Loggar + MCP
    path: 'diagnostics',
    labelKey: 'admin.navDiagnostics',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner,
  },
  {
    // DatHost-budget: saldo, serverkostnad per orsak, månad mot budget
    path: 'dathost-budget',
    labelKey: 'admin.dathostBudget.nav',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner || p.hasAnyCommand('admin.configure'),
  },
  {
    // GSLT (game-server login token) status + schemalagd keepalive
    path: 'gslt',
    labelKey: 'admin.gslt.nav',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner,
  },
  {
    // NUC15-host: CS2/HLAE-versioner, nattlig uppdatering, voice-lyssnare
    path: 'host',
    labelKey: 'admin.host.nav',
    group: 'SYSTEM',
    groupKey: 'admin.groupSystem',
    check: (p) => p.isOwner,
  },
];

/**
 * Sub-tabs for the merged "Åtkomst & Roller" section. Each keeps the
 * exact permission check that previously gated its standalone sidebar tab,
 * so a user only sees the sub-tabs they could reach before.
 */
export const ACCESS_ROLES_SUBTABS = [
  { key: 'access', labelKey: 'admin.subAccess', check: (p) => p.isOwner || p.hasAnyCommand('access.grant') },
  { key: 'invites', labelKey: 'admin.subInvites', check: (p) => p.isOwner || p.hasAnyCommand('access.grant') },
  { key: 'permissions', labelKey: 'admin.subPermissions', check: (p) => p.isOwner || p.hasAnyCommand('admin.permissions') },
  { key: 'web-roles', labelKey: 'admin.subWebRoles', check: (p) => p.isOwner || p.hasAnyCommand('admin.permissions') },
  { key: 'users', labelKey: 'admin.subUsers', check: (p) => p.isOwner || p.hasAnyCommand('admin.permissions') },
];

/**
 * Sub-tabs for the merged "Diagnostik" section. Both are owner-only,
 * matching their previous standalone sidebar tabs.
 */
export const DIAGNOSTICS_SUBTABS = [
  { key: 'logs', labelKey: 'admin.subLogs', check: (p) => p.isOwner },
  { key: 'mcp', labelKey: 'admin.subMcp', check: (p) => p.isOwner },
];

export default ADMIN_SECTIONS;
